import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Phone, Loader2, CheckCircle2, Globe, ShieldCheck, AlertCircle, Eye, EyeOff, RefreshCcw, Key, KeyRound } from 'lucide-react';
import { authApi } from '../services/authService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthModal = ({ onClose, initialMode = 'login', initialMessage, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [systemMessage, setSystemMessage] = useState(initialMessage || '');
  const [otpSent, setOtpSent] = useState(false);
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [resetOtpSent, setResetOtpSent] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timezone: '',
    password: '',
    confirmPassword: '',
    otp: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: 'bg-gray-200' });

  const checkPasswordStrength = (pass) => {
    let score = 0;
    if (pass.length >= 8) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/[a-z]/.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    let label = 'Weak';
    let color = 'bg-secondary';
    if (score === 3) { label = 'Medium'; color = 'bg-yellow-500'; }
    if (score === 4) { label = 'Strong'; color = 'bg-green-500'; }
    if (pass.length === 0) { label = ''; color = 'bg-gray-200'; }
    setPasswordStrength({ score, label, color });
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const res = await authApi.resendVerification(formData.email);
      if (res.success) setSystemMessage("Verification OTP resent successfully. Please check your email.");
      else alert(res.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifySignupOTP = async () => {
    setLoading(true);
    setSystemMessage('');
    setErrors({});
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp: formData.otp })
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        setSystemMessage('Email verified successfully! You can now login.');
        setTimeout(() => {
          setMode('login');
          setSignupOtpSent(false);
          setSubmitted(false);
          setFormData(prev => ({ ...prev, otp: '', password: '' }));
        }, 2000);
      } else {
        setErrors({ otp: data.detail || 'Invalid OTP. Please try again.' });
      }
    } catch (err) {
      setErrors({ otp: 'Failed to verify OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }
    
    setLoading(true);
    setSystemMessage('');
    setErrors({});
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/request-password-change-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await res.json();
      
      if (res.ok) {
        setOtpSent(true);
        setSystemMessage('OTP sent to your email address. Please check your inbox.');
      } else {
        setErrors({ email: data.detail || 'Failed to send OTP' });
      }
    } catch (err) {
      setErrors({ email: 'Failed to send OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResetOTP = async () => {
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }
    
    setLoading(true);
    setSystemMessage('');
    setErrors({});
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await res.json();
      
      if (res.ok) {
        setResetOtpSent(true);
        setSystemMessage('If that email exists, a reset OTP has been sent. Please check your inbox.');
      } else {
        setErrors({ email: data.detail || 'Failed to send OTP' });
      }
    } catch (err) {
      setErrors({ email: 'Failed to send OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordWithOTP = async () => {
    if (formData.newPassword !== formData.confirmNewPassword) {
      setErrors({ confirmNewPassword: 'Passwords do not match' });
      return;
    }
    
    setLoading(true);
    setSystemMessage('');
    setErrors({});
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp,
          newPassword: formData.newPassword
        })
      });
      const data = await res.json();
      
      if (res.ok) {
        setSystemMessage('Password reset successfully! Please login with your new password.');
        setTimeout(() => {
          setMode('login');
          setResetOtpSent(false);
          setFormData(prev => ({ ...prev, otp: '', newPassword: '', confirmNewPassword: '' }));
        }, 2000);
      } else {
        setErrors({ otp: data.detail || 'Failed to reset password' });
      }
    } catch (err) {
      setErrors({ otp: 'Failed to reset password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePasswordWithOTP = async () => {
    if (formData.newPassword !== formData.confirmNewPassword) {
      setErrors({ confirmNewPassword: 'Passwords do not match' });
      return;
    }
    
    setLoading(true);
    setSystemMessage('');
    setErrors({});
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-otp-change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp,
          newPassword: formData.newPassword
        })
      });
      const data = await res.json();
      
      if (res.ok) {
        setSystemMessage('Password changed successfully! Please login with your new password.');
        setTimeout(() => {
          setMode('login');
          setOtpSent(false);
          setFormData(prev => ({ ...prev, otp: '', newPassword: '', confirmNewPassword: '' }));
        }, 2000);
      } else {
        setErrors({ otp: data.detail || 'Failed to change password' });
      }
    } catch (err) {
      setErrors({ otp: 'Failed to change password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSystemMessage('');
    setErrors({});
    
    try {
      if (mode === 'signup') {
        if (signupOtpSent) {
          // Verify OTP
          await handleVerifySignupOTP();
          return;
        }
        // Create account and send OTP
        const res = await authApi.signup(formData);
        if (res.success) {
          setSignupOtpSent(true);
          setSystemMessage('Account created! Please enter the OTP sent to your email to verify your account.');
        } else {
          setErrors({ email: res.message });
        }
      } else if (mode === 'login') {
        // Check if this is admin login
        if (formData.email.toLowerCase() === 'preciseotaxservices@gmail.com') {
          try {
            const adminRes = await fetch(`${BACKEND_URL}/api/admin/login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: formData.email, password: formData.password })
            });
            const adminData = await adminRes.json();
            
            if (adminRes.ok && adminData.access_token) {
              localStorage.setItem('preciseo_admin_token', adminData.access_token);
              onClose();
              navigate('/admin');
              return;
            } else {
              setErrors({ password: 'Invalid email or password. Please check your credentials.' });
              setLoading(false);
              return;
            }
          } catch (err) {
            setErrors({ password: 'Login failed. Please try again.' });
            setLoading(false);
            return;
          }
        }
        
        // Regular user login
        const res = await authApi.login(formData.email, formData.password || '');
        if (res.success) {
          onClose();
          if (onLoginSuccess) onLoginSuccess(res.user);
          navigate('/dashboard');
        } else {
          if (res.message === 'UNVERIFIED') {
            setSystemMessage('Please verify your account using the OTP sent to your email before logging in.');
          } else if (res.message === 'Invalid credentials') {
            setErrors({ password: 'Invalid email or password. Please check your credentials.' });
          } else {
            setErrors({ password: res.message || 'Invalid email or password.' });
          }
        }
      } else if (mode === 'forgot-password') {
        if (resetOtpSent) {
          await handleResetPasswordWithOTP();
        } else {
          await handleRequestResetOTP();
        }
        return;
      } else if (mode === 'change-password') {
        if (otpSent) {
          await handleChangePasswordWithOTP();
        } else {
          await handleRequestOTP();
        }
        return;
      }
    } catch (err) {
      setSystemMessage("Unable to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') setFormData(prev => ({ ...prev, [name]: value.replace(/\D/g, '') }));
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === 'password' || name === 'newPassword') checkPasswordStrength(value);
    }
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const isFormValid = () => {
    if (mode === 'login') return validateEmail(formData.email) && formData.password;
    if (mode === 'forgot-password') {
      if (!resetOtpSent) return validateEmail(formData.email);
      return formData.otp && formData.newPassword && formData.newPassword === formData.confirmNewPassword && passwordStrength.score >= 3;
    }
    if (mode === 'change-password') {
      if (!otpSent) return validateEmail(formData.email);
      return formData.otp && formData.newPassword && formData.newPassword === formData.confirmNewPassword && passwordStrength.score >= 3;
    }
    if (mode === 'signup') {
      if (signupOtpSent) return formData.otp && formData.otp.length === 6;
      return (
        formData.firstName && formData.lastName && formData.phone && 
        validateEmail(formData.email) && formData.timezone && 
        passwordStrength.score >= 4 && formData.password === formData.confirmPassword
      );
    }
    return false;
  };

  const resetMode = (newMode) => {
    setMode(newMode);
    setErrors({});
    setSystemMessage('');
    setOtpSent(false);
    setSignupOtpSent(false);
    setResetOtpSent(false);
    setFormData(prev => ({ ...prev, otp: '', newPassword: '', confirmNewPassword: '' }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-primary/60 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-10">
          <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-primary transition-colors p-2 hover:bg-gray-50 rounded-full"><X size={24} /></button>
          <AnimatePresence mode="wait">
            <motion.div key={mode + (signupOtpSent ? '-otp' : '') + (resetOtpSent ? '-reset-otp' : '')} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-secondary/10 rounded-[1.25rem] text-secondary mb-6">
                  {mode === 'login' ? <Lock size={32} /> : mode === 'signup' ? (signupOtpSent ? <Key size={32} /> : <User size={32} />) : mode === 'change-password' ? <KeyRound size={32} /> : <Key size={32} />}
                </div>
                <h2 className="text-4xl font-black font-heading text-primary tracking-tight">
                  {mode === 'login' ? 'Welcome Back' : 
                   mode === 'signup' ? (signupOtpSent ? 'Verify Email' : 'Create Account') : 
                   mode === 'change-password' ? 'Change Password' : 
                   (resetOtpSent ? 'Reset Password' : 'Forgot Password')}
                </h2>
                <p className="text-gray-400 font-bold text-base mt-2">
                  {mode === 'login' ? 'Securely access your tax dashboard' : 
                   mode === 'signup' ? (signupOtpSent ? 'Enter the 6-digit OTP sent to your email' : 'Join thousands of satisfied clients today') : 
                   mode === 'change-password' ? (otpSent ? 'Enter the OTP sent to your email' : 'We will send an OTP to verify your identity') : 
                   (resetOtpSent ? 'Enter the OTP and your new password' : 'Enter your email to receive a reset OTP')}
                </p>
              </div>

              {systemMessage && (
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className={`mb-6 p-4 rounded-2xl border-l-4 ${systemMessage.includes('successfully') || systemMessage.includes('verified') || systemMessage.includes('created') || systemMessage.includes('OTP sent') || systemMessage.includes('reset OTP') ? 'bg-green-50 border-green-500 text-green-700' : 'bg-secondary/5 border-secondary text-secondary'} font-bold text-sm`}>
                  {systemMessage}
                </motion.div>
              )}

              {errors.password && (
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-6 p-4 rounded-2xl border-l-4 bg-red-50 border-red-500 text-red-700 font-bold text-sm flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {errors.password}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Signup form fields */}
                {mode === 'signup' && !signupOtpSent && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative"><User className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" /><input type="text" name="firstName" required placeholder="First Name" className="w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5" value={formData.firstName} onChange={handleChange} /></div>
                      <div className="relative"><User className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" /><input type="text" name="lastName" required placeholder="Last Name" className="w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5" value={formData.lastName} onChange={handleChange} /></div>
                    </div>
                    <div className="relative"><Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" /><input type="tel" name="phone" required placeholder="Phone Number (Numbers Only)" className="w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5" value={formData.phone} onChange={handleChange} /></div>
                  </>
                )}

                {/* OTP input for signup verification */}
                {mode === 'signup' && signupOtpSent && (
                  <div className="relative">
                    <Key className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input type="text" name="otp" required placeholder="Enter 6-digit OTP" maxLength={6} className={`w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5 tracking-widest text-center text-xl ${errors.otp ? 'border-red-500' : ''}`} value={formData.otp} onChange={handleChange} />
                    {errors.otp && <p className="text-red-500 text-xs mt-1 ml-2">{errors.otp}</p>}
                  </div>
                )}
                
                {/* Email field */}
                {!(mode === 'signup' && signupOtpSent) && !(mode === 'change-password' && otpSent) && !(mode === 'forgot-password' && resetOtpSent) && (
                  <div className="relative">
                    <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input type="email" name="email" required placeholder="Email Address" className={`w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5 ${errors.email ? 'border-red-500' : ''}`} value={formData.email} onChange={handleChange} />
                    {errors.email && <p className="text-red-500 text-xs mt-1 ml-2">{errors.email}</p>}
                  </div>
                )}

                {/* Timezone for signup */}
                {mode === 'signup' && !signupOtpSent && (
                  <div className="relative"><Globe className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" /><select name="timezone" required className="w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary bg-white cursor-pointer border-gray-200 focus:border-primary" value={formData.timezone} onChange={handleChange}><option value="">Select Time Zone</option><option value="PST">PST</option><option value="CST">CST</option><option value="MST">MST</option><option value="EST">EST</option></select></div>
                )}

                {/* Password field for login and signup */}
                {(mode === 'login' || (mode === 'signup' && !signupOtpSent)) && (
                  <div className="relative">
                    <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input type={showPassword ? "text" : "password"} name="password" required placeholder="Password" className={`w-full pl-14 pr-14 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5`} value={formData.password} onChange={handleChange} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary">{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                  </div>
                )}

                {/* Password strength for signup */}
                {mode === 'signup' && !signupOtpSent && formData.password && (
                  <div className="px-1"><div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex">{[1, 2, 3, 4].map(s => <div key={s} className={`h-full flex-1 border-r border-white last:border-0 transition-all ${passwordStrength.score >= s ? passwordStrength.color : 'bg-transparent'}`} />)}</div><p className="text-[10px] font-black uppercase text-gray-400 mt-1">Strength: {passwordStrength.label}</p></div>
                )}

                {/* Confirm password for signup */}
                {mode === 'signup' && !signupOtpSent && (
                  <div className="relative"><ShieldCheck className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" /><input type="password" name="confirmPassword" required placeholder="Confirm Password" className="w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary" value={formData.confirmPassword} onChange={handleChange} /></div>
                )}

                {/* OTP and new password fields for forgot-password */}
                {mode === 'forgot-password' && resetOtpSent && (
                  <>
                    <div className="relative">
                      <Key className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input type="text" name="otp" required placeholder="Enter 6-digit OTP" maxLength={6} className={`w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5 tracking-widest text-center text-xl ${errors.otp ? 'border-red-500' : ''}`} value={formData.otp} onChange={handleChange} />
                      {errors.otp && <p className="text-red-500 text-xs mt-1 ml-2">{errors.otp}</p>}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input type={showPassword ? "text" : "password"} name="newPassword" required placeholder="New Password" className="w-full pl-14 pr-14 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5" value={formData.newPassword} onChange={handleChange} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary">{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                    </div>
                    {formData.newPassword && (
                      <div className="px-1"><div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex">{[1, 2, 3, 4].map(s => <div key={s} className={`h-full flex-1 border-r border-white last:border-0 transition-all ${passwordStrength.score >= s ? passwordStrength.color : 'bg-transparent'}`} />)}</div><p className="text-[10px] font-black uppercase text-gray-400 mt-1">Strength: {passwordStrength.label}</p></div>
                    )}
                    <div className="relative">
                      <ShieldCheck className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input type="password" name="confirmNewPassword" required placeholder="Confirm New Password" className={`w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary ${errors.confirmNewPassword ? 'border-red-500' : ''}`} value={formData.confirmNewPassword} onChange={handleChange} />
                      {errors.confirmNewPassword && <p className="text-red-500 text-xs mt-1 ml-2">{errors.confirmNewPassword}</p>}
                    </div>
                  </>
                )}

                {/* Change Password with OTP fields */}
                {mode === 'change-password' && otpSent && (
                  <>
                    <div className="relative">
                      <Key className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input type="text" name="otp" required placeholder="Enter 6-digit OTP" maxLength={6} className={`w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5 tracking-widest text-center text-xl ${errors.otp ? 'border-red-500' : ''}`} value={formData.otp} onChange={handleChange} />
                      {errors.otp && <p className="text-red-500 text-xs mt-1 ml-2">{errors.otp}</p>}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input type={showPassword ? "text" : "password"} name="newPassword" required placeholder="New Password" className="w-full pl-14 pr-14 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/5" value={formData.newPassword} onChange={handleChange} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary">{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                    </div>
                    {formData.newPassword && (
                      <div className="px-1"><div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex">{[1, 2, 3, 4].map(s => <div key={s} className={`h-full flex-1 border-r border-white last:border-0 transition-all ${passwordStrength.score >= s ? passwordStrength.color : 'bg-transparent'}`} />)}</div><p className="text-[10px] font-black uppercase text-gray-400 mt-1">Strength: {passwordStrength.label}</p></div>
                    )}
                    <div className="relative">
                      <ShieldCheck className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input type="password" name="confirmNewPassword" required placeholder="Confirm New Password" className={`w-full pl-14 pr-5 py-4 border rounded-2xl outline-none font-bold text-primary border-gray-200 focus:border-primary ${errors.confirmNewPassword ? 'border-red-500' : ''}`} value={formData.confirmNewPassword} onChange={handleChange} />
                      {errors.confirmNewPassword && <p className="text-red-500 text-xs mt-1 ml-2">{errors.confirmNewPassword}</p>}
                    </div>
                  </>
                )}

                {/* Login links */}
                {mode === 'login' && (
                  <div className="flex justify-between">
                    <button type="button" onClick={() => resetMode('forgot-password')} className="text-secondary font-black text-sm hover:underline">Forgot Password?</button>
                    <button type="button" onClick={() => resetMode('change-password')} className="text-primary font-black text-sm hover:underline">Change Password</button>
                  </div>
                )}

                {/* Resend OTP for signup */}
                {mode === 'signup' && signupOtpSent && (
                  <div className="text-center">
                    <button type="button" onClick={handleResend} disabled={resendLoading} className="flex items-center justify-center mx-auto text-secondary font-black hover:underline disabled:opacity-50">
                      {resendLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <RefreshCcw size={16} className="mr-2" />}
                      Resend OTP
                    </button>
                  </div>
                )}

                <button type="submit" disabled={loading || !isFormValid()} className="w-full bg-secondary hover:bg-secondary-hover disabled:bg-gray-300 text-white font-black text-xl py-5 rounded-2xl shadow-2xl transition-all flex justify-center items-center mt-6 active:scale-95" data-testid="auth-submit-btn">
                  {loading ? <Loader2 className="animate-spin h-7 w-7" /> : 
                    mode === 'login' ? 'Login' : 
                    mode === 'signup' ? (signupOtpSent ? 'Verify OTP' : 'Create Account') : 
                    mode === 'change-password' ? (otpSent ? 'Change Password' : 'Send OTP') :
                    mode === 'forgot-password' ? (resetOtpSent ? 'Reset Password' : 'Send Reset OTP') :
                    'Submit'}
                </button>
              </form>
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-gray-500 font-bold text-base">
                  {mode === 'login' ? "Don't have an account?" : "Ready to sign in?"}
                  <button onClick={() => resetMode(mode === 'signup' ? 'login' : 'signup')} className="ml-3 text-secondary font-black hover:underline">{mode === 'signup' ? 'Back to Login' : mode === 'login' ? 'Create an Account' : 'Back to Login'}</button>
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthModal;
